import React from 'react';
import { useParams } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';

import MDEditor from '@uiw/react-md-editor';

import CustomButton from 'components/button/CustomButton';
import Circular from 'components/common/CustomCircular';
import KnzViewer from 'components/knz/KnzViewer';
import { LanguageChip, WorkspaceFileSelectDialog } from 'components/training';
import {
  LearnQuestionDetail,
  VerifyAnswerResult,
  getLearnQuestion,
  listLearnAssignment,
  verifyAnswer,
} from 'stores/TrainingQuestionStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import LearnBreadcrumbs from './components/LearnBreadcrumbs';
import VerifyResultCard from './components/VerifyResultCard';

/**
 * 設問詳細・解答（受講・API6353/6354）。
 * 設問本文（KNZ=KnzViewer / FORM=md）＋言語別クリア状況＋解答ファイル選択＋検証結果表示。
 */
const QuestionDetail: React.FC = () => {
  const { assignmentId, questionId } = useParams<{ assignmentId: string; questionId: string }>();
  const { mode } = useColorScheme();

  const [question, setQuestion] = React.useState<LearnQuestionDetail | null>(null);
  const [assignmentName, setAssignmentName] = React.useState<string>('');
  const [loading, setLoading] = React.useState(true);

  const [selectedFile, setSelectedFile] = React.useState<{ fileId: string; name: string } | null>(
    null,
  );
  const [fileDialogOpen, setFileDialogOpen] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [verifyElapsed, setVerifyElapsed] = React.useState(0);
  const [verifyResult, setVerifyResult] = React.useState<VerifyAnswerResult | null>(null);
  const [openAnswerExt, setOpenAnswerExt] = React.useState<string | null>(null);

  // 検証中は経過秒数を 1 秒ごとにカウントアップする（workspace の実行中表示と同様）
  React.useEffect(() => {
    if (!verifying) {
      setVerifyElapsed(0);
      return;
    }
    setVerifyElapsed(0);
    const timer = setInterval(() => setVerifyElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [verifying]);

  const loadQuestion = React.useCallback(() => {
    if (!questionId) return;
    setLoading(true);
    getLearnQuestion(questionId)
      .then(setQuestion)
      .catch(() => errorDefault('設問の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [questionId]);

  // クリア後の再取得（全画面ローディングにせず、検証結果表示を維持したまま更新する）
  const reloadQuestionQuiet = React.useCallback(() => {
    if (!questionId) return;
    getLearnQuestion(questionId)
      .then(setQuestion)
      .catch(() => errorDefault('設問の再取得に失敗しました'));
  }, [questionId]);

  React.useEffect(() => {
    loadQuestion();
  }, [loadQuestion]);

  // API6353 は課題名を返さないため、割当課題一覧（API6351）から課題名を解決する（パンくず用）
  React.useEffect(() => {
    if (!assignmentId) return;
    listLearnAssignment()
      .then((assignments) => {
        const found = assignments.find((a) => a.assignmentId === assignmentId);
        setAssignmentName(found?.name ?? '');
      })
      .catch(() => {
        /* パンくずのベストエフォート。失敗時は「設問一覧」表示 */
      });
  }, [assignmentId]);

  // 設問の対応言語（拡張子）— 解答ファイル選択の絞り込みに使う
  const supportedExtensions = React.useMemo(
    () => question?.languages.map((l) => l.extension) ?? [],
    [question],
  );

  const handleVerify = async () => {
    if (!question || !selectedFile) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await verifyAnswer(question.questionId, selectedFile.fileId);
      setVerifyResult(result);
      if (result.cleared) {
        successDefault(`${result.extension} をクリアしました`);
        // クリアで正解コードが開放されるため再取得（全画面ローディングにしない）。
        // クリアした言語の正解コードをすぐ見られるよう展開する。
        setOpenAnswerExt(result.extension);
        reloadQuestionQuiet();
      }
    } catch (e) {
      errorDefault('検証に失敗しました', toErrorDetail(e));
    } finally {
      setVerifying(false);
    }
  };

  if (loading || !question) {
    return (
      <Box sx={{ height: '100%' }}>
        <Circular open={loading} />
      </Box>
    );
  }

  const isKnz = question.sourceType === 'KNZ';

  return (
    <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* 検証中オーバーレイ（workspace の実行中表示と同じ見た目・経過秒数）。
          スクロールしないこの外枠に固定し、常に画面中央に表示する。 */}
      {verifying && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            bgcolor: 'rgba(0, 0, 0, 0.35)',
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
            検証中... {verifyElapsed}秒
          </Typography>
        </Box>
      )}
      {/* スクロール領域（オーバーレイの下） */}
      <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
        <LearnBreadcrumbs
          items={[
            { label: '研修', to: '/training-learn' },
            { label: '課題', to: '/training-learn/learn-assignment' },
            {
              label: assignmentName || '設問一覧',
              to: `/training-learn/learn-assignment/${assignmentId}`,
            },
            { label: question.title },
          ]}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ minWidth: 0 }} noWrap>
            {question.title}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<LaunchIcon />}
            onClick={() => window.open('/workspace', '_blank')}
            sx={{
              flexShrink: 0,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            workspaceを開く
          </Button>
        </Stack>

        <Stack spacing={2} sx={{ maxWidth: 960 }}>
          {/* 言語別クリア状況 */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              対応言語:
            </Typography>
            {question.languages.map((lang) => (
              <LanguageChip
                key={lang.extension}
                extension={lang.extension}
                cleared={lang.cleared}
              />
            ))}
          </Stack>

          {/* 設問本文 */}
          <Paper
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            {isKnz ? (
              <KnzViewer source={question.content ?? ''} tocMode="ignore" />
            ) : (
              <Box
                data-color-mode={mode === 'dark' ? 'dark' : 'light'}
                sx={{ p: 3, bgcolor: 'transparent' }}
              >
                <MDEditor.Markdown
                  source={question.content ?? ''}
                  style={{ background: 'transparent' }}
                />
              </Box>
            )}
          </Paper>

          <Divider />

          {/* 解答 */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              解答
            </Typography>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 1.5 }}>
              workspace で解答ファイルを作成し、対応言語（{supportedExtensions.join(' / ')}
              ）のファイルを選んで検証してください。
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <CustomButton
                label={selectedFile ? selectedFile.name : '解答ファイルを選択'}
                buttonVariant="secondary"
                icon={<DescriptionOutlinedIcon />}
                onClick={() => setFileDialogOpen(true)}
              />
              <CustomButton
                label="検証"
                buttonVariant="success"
                icon={<PlayArrowIcon />}
                onClick={handleVerify}
                disabled={!selectedFile || verifying}
              />
            </Stack>
          </Box>

          {/* 検証結果 */}
          {verifyResult && (
            <Box>
              {verifyResult.cleared && (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon fontSize="small" />}
                  sx={{ borderRadius: 2, mb: 1.5 }}
                >
                  この言語（{verifyResult.extension}）をクリアしました
                </Alert>
              )}
              <Stack spacing={1.5}>
                {verifyResult.results.map((item, i) => (
                  <VerifyResultCard key={i} index={i} item={item} />
                ))}
              </Stack>
            </Box>
          )}

          <Divider />

          {/* クリア済み言語の正解コード */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              正解コード
            </Typography>
            {question.languages.filter((l) => l.cleared).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                クリアした言語の正解コードがここに表示されます
              </Typography>
            ) : (
              <Stack spacing={1}>
                {question.languages
                  .filter((l) => l.cleared)
                  .map((lang) => (
                    <Box
                      key={lang.extension}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        onClick={() =>
                          setOpenAnswerExt(openAnswerExt === lang.extension ? null : lang.extension)
                        }
                        sx={{
                          px: 1.5,
                          py: 1,
                          cursor: 'pointer',
                          bgcolor: 'action.hover',
                          '&:hover': { bgcolor: 'action.selected' },
                        }}
                      >
                        <CodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Chip
                          size="small"
                          label={lang.extension}
                          sx={{ borderRadius: 1, fontWeight: 600 }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          正解コードを見る
                        </Typography>
                      </Stack>
                      <Collapse in={openAnswerExt === lang.extension}>
                        <Box sx={{ p: 1.5 }}>
                          {lang.answerCode ? (
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: '#0d1117',
                                color: '#e6edf3',
                                fontSize: 12,
                                overflow: 'auto',
                                maxHeight: 400,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {lang.answerCode}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              正解例は現在利用できません（ファイルが削除された可能性があります）
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>

      <WorkspaceFileSelectDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        onSelect={(file) => {
          setSelectedFile({ fileId: file.fileId, name: file.name });
          setFileDialogOpen(false);
        }}
        title="解答ファイルを選択"
        filter={supportedExtensions}
      />
    </Box>
  );
};

export default QuestionDetail;
